import { message } from "@/utils/staticAntd";
import { useIntl } from "@umijs/max";
import { Button, Input, Modal } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { getLatestNpsTime, submitFeedback } from "@/services/stockware/feedback";

const NPS_INTERVAL_DAYS = 30;
const NPS_MIN_USAGE_DAYS = 3;
const NPS_DISMISSED_KEY = "stockware_nps_dismissed_at";

/** 检查是否应显示 NPS 调查 */
async function shouldShowNps(createdAt?: string): Promise<boolean> {
  // 注册不满 N 天的用户不弹
  if (createdAt) {
    const regDate = new Date(createdAt);
    const daysSinceReg =
      (Date.now() - regDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceReg < NPS_MIN_USAGE_DAYS) return false;
  }

  // 本地关闭过的 30 天内不再弹
  const dismissedAt = localStorage.getItem(NPS_DISMISSED_KEY);
  if (dismissedAt) {
    const daysSinceDismiss =
      (Date.now() - new Date(dismissedAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceDismiss < NPS_INTERVAL_DAYS) return false;
  }

  // 服务端最近 30 天提交过 NPS 也不弹
  try {
    const res = await getLatestNpsTime();
    if (res?.latestNpsTime) {
      const daysSinceLast =
        (Date.now() - new Date(res.latestNpsTime).getTime()) /
        (1000 * 60 * 60 * 24);
      if (daysSinceLast < NPS_INTERVAL_DAYS) return false;
    }
  } catch {
    // 接口失败不阻塞，默认不弹
    return false;
  }

  return true;
}

interface NpsSurveyProps {
  /** 用户注册时间，用于判断使用天数 */
  userCreatedAt?: string;
}

const NpsSurvey: React.FC<NpsSurveyProps> = ({ userCreatedAt }) => {
  const intl = useIntl();
  const [visible, setVisible] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // 延迟 5 秒后检查，避免页面加载时干扰
    const timer = setTimeout(async () => {
      const show = await shouldShowNps(userCreatedAt);
      if (show) setVisible(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [userCreatedAt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(NPS_DISMISSED_KEY, new Date().toISOString());
    setVisible(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (score === null) return;
    setSubmitting(true);
    try {
      await submitFeedback({
        category: "NPS",
        content: comment || `NPS Score: ${score}`,
        npsScore: score,
        pageUrl: window.location.pathname,
        browserInfo: navigator.userAgent,
      });
      message.success(intl.formatMessage({ id: "nps.thanks" }));
      setVisible(false);
    } catch {
      // 静默处理
    } finally {
      setSubmitting(false);
    }
  }, [score, comment, intl]);

  if (!visible) return null;

  return (
    <Modal
      open={visible}
      onCancel={handleDismiss}
      footer={null}
      width={480}
      centered
      closable
      mask={{ closable: false }}
    >
      <div style={{ textAlign: "center", padding: "8px 0" }}>
        <h3 style={{ marginBottom: 8 }}>
          {intl.formatMessage({ id: "nps.title" })}
        </h3>
        <p style={{ color: "#666", marginBottom: 20 }}>
          {intl.formatMessage({ id: "nps.subtitle" })}
        </p>

        {/* 0-10 评分按钮 */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 6,
            marginBottom: 8,
          }}
        >
          {Array.from({ length: 11 }, (_, i) => (
            <Button
              key={i}
              type={score === i ? "primary" : "default"}
              size="middle"
              onClick={() => setScore(i)}
              style={{
                minWidth: 36,
                fontWeight: score === i ? 700 : 400,
              }}
            >
              {i}
            </Button>
          ))}
        </div>

        {/* 标签：不太可能 / 非常可能 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            color: "#999",
            fontSize: 12,
            marginBottom: 20,
            padding: "0 4px",
          }}
        >
          <span>{intl.formatMessage({ id: "nps.unlikely" })}</span>
          <span>{intl.formatMessage({ id: "nps.likely" })}</span>
        </div>

        {/* 选分后展开评论框 */}
        {score !== null && (
          <>
            <Input.TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={intl.formatMessage({ id: "nps.comment.placeholder" })}
              rows={3}
              maxLength={500}
              showCount
              style={{ marginBottom: 16 }}
            />
            <Button
              type="primary"
              block
              loading={submitting}
              onClick={handleSubmit}
            >
              {intl.formatMessage({ id: "feedback.submit" })}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default NpsSurvey;
